/**
 * PhotoReorderList — dnd-kit thumbnail reorder grid (EPIC-006 Phase 7,
 * Task 7.2; story 006-011 dnd).
 *
 * dnd-kit's first integration in the app:
 *   - `DndContext` with a Pointer sensor AND a Keyboard sensor
 *     (`sortableKeyboardCoordinates`) so the gallery reorders via keyboard too;
 *   - `SortableContext` + `useSortable` per thumbnail;
 *   - each thumbnail has a drag handle with an `aria-label`.
 *
 * On drop the parent reorders OPTIMISTICALLY (it owns the `photos` state) and
 * fires `onReorder(orderedIds)` → `POST /sellers/me/products/{id}/photos/reorder`.
 * On failure the parent reverts and toasts (handled in `ProductEditorPage`).
 *
 * Respects `prefers-reduced-motion`: dnd-kit's CSS transform still applies, but
 * we suppress the spring transition under the media query.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "@garden/ui";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
} from "@/features/seller-dashboard/api";

/** One photo in the gallery (the `id` is the stable dnd key). */
export interface EditorPhoto {
  /**
   * Stable dnd key. For a SAVED photo this is the real `ProductImage.id`
   * (so it also drives the `ordered_image_ids` reorder payload). For a
   * newly-uploaded-but-unsaved photo there is no image id yet, so the clean
   * s3 key stands in as the dnd id until the PATCH save returns its image id.
   */
  id: string;
  /**
   * The real `ProductImage.id` once the photo is saved; `null` for an
   * unsaved upload. Reorder is only enabled when EVERY photo has an id.
   */
  imageId: string | null;
  /** The clean s3 key — the `s3_key_original` in the PATCH `photos` payload. */
  s3KeyOriginal: string;
  /** Display thumbnail URL (local object URL or a resolved CDN URL). */
  previewUrl: string;
}

interface SortableThumbProps {
  photo: EditorPhoto;
  index: number;
  canReorder: boolean;
  onRemove: (id: string) => void;
}

function SortableThumb({
  photo,
  index,
  canReorder,
  onRemove,
}: SortableThumbProps): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id, disabled: !canReorder });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    // `prefers-reduced-motion` users get no spring animation.
    transition: transition ?? undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid="photo-thumb"
      className="relative flex size-24 items-center justify-center overflow-hidden rounded-[var(--g-radius)] border border-border bg-muted motion-reduce:!transition-none"
    >
      <img
        src={photo.previewUrl}
        alt=""
        className="size-full object-cover"
        draggable={false}
      />
      {/* Drag handle — disabled until every photo is saved (has an image id). */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        disabled={!canReorder}
        aria-label={t("photos.dragHandle", { index: index + 1 })}
        data-testid={`photo-drag-${photo.id}`}
        className="absolute left-1 top-1 inline-flex size-7 cursor-grab items-center justify-center rounded-[var(--g-radius)] bg-[var(--g-ground)]/80 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Icon name="menu" size={14} />
      </button>
      {/* Remove */}
      <button
        type="button"
        onClick={() => onRemove(photo.id)}
        aria-label={t("photos.remove")}
        data-testid={`photo-remove-${photo.id}`}
        className="absolute right-1 top-1 inline-flex size-7 items-center justify-center rounded-[var(--g-radius)] bg-[var(--g-ground)]/80 text-[var(--g-error)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icon name="x" size={14} />
      </button>
    </li>
  );
}

export interface PhotoReorderListProps {
  photos: EditorPhoto[];
  /** Called with the new id order after a successful drag. */
  onReorder: (orderedIds: string[]) => void;
  onRemove: (id: string) => void;
}

export function PhotoReorderList({
  photos,
  onReorder,
  onRemove,
}: PhotoReorderListProps): ReactElement | null {
  registerSellerDashboardI18n();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Reorder posts `ProductImage.id` UUIDs, so it is only enabled once EVERY
  // photo is saved (has an `imageId`). A gallery with an unsaved upload can be
  // reordered locally only after the next save round-trip assigns ids.
  const canReorder = photos.length > 1 && photos.every((p) => p.imageId !== null);

  if (photos.length === 0) return null;

  const handleDragEnd = (event: DragEndEvent): void => {
    if (!canReorder) return;
    const { active, over } = event;
    if (over === null || active.id === over.id) return;
    const oldIndex = photos.findIndex((p) => p.id === active.id);
    const newIndex = photos.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(photos, oldIndex, newIndex);
    // The dnd id IS the `ProductImage.id` for saved photos → the reorder
    // payload is the image UUIDs the endpoint validates.
    onReorder(next.map((p) => p.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={photos.map((p) => p.id)}
        strategy={rectSortingStrategy}
      >
        <ul className="flex flex-wrap gap-3" data-testid="photo-reorder-list">
          {photos.map((photo, i) => (
            <SortableThumb
              key={photo.id}
              photo={photo}
              index={i}
              canReorder={canReorder}
              onRemove={onRemove}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

export default PhotoReorderList;
